import { getRootEnv } from '@bringg-frontend/bringg-web-infra';
import { LocationEvent, Run, RunHistoryEvent } from '@bringg/types';
import { getRoot } from 'mobx-easy';
import { reaction } from 'mobx';
import { RunQueryOptions, UpdateRunOptions } from '@bringg/dashboard-sdk/dist/Run/run-types';
import { TaskVehiclePair } from '@bringg/dashboard-sdk/dist/Run/Service/runs.service';
import { DataOptions, MappedStorableItem } from '@bringg/dashboard-sdk/dist/data-entity';
import { CommonOptions } from '@bringg/dashboard-sdk/dist/Core/RouteGenerator';
import {
	RunBulkCloseResponse,
	RunMarkDonePlanningResponse,
	RunsResponse
} from '@bringg/dashboard-sdk/dist/Run/runs.consts';

import RootStore from 'bringg-web/stores/root-store';

export default class RunsStoreAdapter {
	private static readonly DEFAULT_PAGE_SIZE = 200;
	private readonly api = getRootEnv().dashboardSdk.sdk.v2.runs;

	public afterRootInit() {
		reaction(
			() => getRoot<RootStore>().views.authStore.isLoggedIn,
			() => {
				this.api.updateStoreOnEvents();
			}
		);
	}

	public onCreate(callback: (mappedStorableItem: MappedStorableItem<Run>) => void) {
		return this.api.onCreate(callback);
	}

	public onUpdate(callback: (mappedStorableItem: MappedStorableItem<Run>) => void) {
		return this.api.onUpdate(callback);
	}

	public onDelete(callback: (mappedStorableItem: MappedStorableItem<Run>) => void) {
		return this.api.onDelete(callback);
	}

	public async loadMany(
		query: RunQueryOptions,
		options?: DataOptions,
		commonOptions?: CommonOptions
	): Promise<Run[]> {
		if (query?.ids && query?.ids.length === 0) {
			return [];
		}
		return this.api.loadMany(
			{
				items: RunsStoreAdapter.DEFAULT_PAGE_SIZE,
				...query
			},
			options,
			commonOptions
		);
	}

	public async loadAllByIds(ids: number[]): Promise<Run[]> {
		if (!ids.length) {
			return [];
		}
		return this.api.loadMany({ ids });
	}

	public async load(id: number, force?: boolean): Promise<Run> {
		return this.api.load(id, { useCache: !force });
	}

	public get(id: number): Run {
		return this.api.get(id);
	}

	public getByIds(ids: number[]): Run[] {
		return this.api.getByIds(ids);
	}

	public add(run: Run) {
		this.api.add(run);
	}

	public remove(id: number) {
		this.api.remove(id);
	}

	public getAll(): Record<number, Run> {
		return this.api.getAllMap();
	}

	public async update(id: number, options: UpdateRunOptions): Promise<Run> {
		return this.api.update(id, options);
	}

	public async assignVehicles(runId: number, vehicleId: number, tasksIds: TaskVehiclePair[]) {
		return this.api.assignVehicles(runId, vehicleId, tasksIds);
	}

	public async unassignVehicles(runId: number) {
		return this.api.unassignVehicles(runId);
	}

	public async bulkClose(runIds: number[], commonOptions?: CommonOptions): Promise<RunBulkCloseResponse> {
		return this.api.bulkClose(runIds, commonOptions);
	}

	public async markDonePlanning(
		runIds: number[],
		commonOptions?: CommonOptions
	): Promise<RunMarkDonePlanningResponse> {
		return this.api.markDonePlanning(runIds, commonOptions);
	}

	public async getHistory(id: number, commonOptions?: CommonOptions): Promise<RunHistoryEvent[]> {
		return this.api.getHistory(id, commonOptions);
	}

	public async getLocations(id: number, commonOptions?: CommonOptions): Promise<LocationEvent[]> {
		return this.api.getLocations(id, commonOptions);
	}

	public async getAllByPage(options: RunQueryOptions, commonOptions?: CommonOptions): Promise<RunsResponse> {
		return this.api.getAllByPage(options, commonOptions);
	}
}
